import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSectionOrder } from '../../contexts/SectionOrderContext';
import { useTheme } from '../../theme';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  sectionId: string;
  defaultExpanded?: boolean;
  priority?: 'high' | 'medium' | 'low';
  dragHandleProps?: any;
  isDragging?: boolean;
  icon?: React.ReactNode;
  isEmpty?: boolean;
  emptyMessage?: string;
  headerActions?: React.ReactNode;
  className?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  sectionId,
  defaultExpanded = false,
  priority = 'medium',
  dragHandleProps,
  isDragging = false,
  icon,
  isEmpty = false,
  emptyMessage = 'No information available',
  headerActions,
  className = '',
}) => {
  const { collapsibleStates, setSectionCollapsed } = useSectionOrder();

  // Get expanded state from context, fallback to defaultExpanded if not found
  const isExpanded = collapsibleStates[sectionId] !== undefined
    ? !collapsibleStates[sectionId] // collapsibleStates stores collapsed state, so we invert it
    : defaultExpanded;

  const toggleExpanded = () => {
    // Store collapsed state (inverted from expanded state)
    setSectionCollapsed(sectionId, isExpanded);
  };

  // Animation variants for Framer Motion - smooth unfolding motion
  const sectionVariants = {
    expanded: {
      height: "auto",
      opacity: 1
    },
    collapsed: {
      height: 0,
      opacity: 0
    }
  };

  const sectionTransition = {
    duration: 0.3,
    ease: "easeOut" as const,
    height: {
      duration: 0.4
    },
    opacity: {
      duration: 0.25,
      delay: 0.1
    }
  };

  const chevronVariants = {
    expanded: { rotate: 180 },
    collapsed: { rotate: 0 }
  };

  // Build CSS classes
  const baseClassName = 'collapsible-section';
  const priorityClassName = `collapsible-section--${priority}`;
  const draggingClassName = isDragging ? 'collapsible-section--dragging' : '';
  const emptyClassName = isEmpty ? 'collapsible-section--empty' : '';
  const finalClassName = `${baseClassName} ${priorityClassName} ${draggingClassName} ${emptyClassName} ${className}`.trim();

  return (
    <div
      className={finalClassName}
      id={sectionId}
    >
      {/* Header container with optional drag handle */}
      <div className="collapsible-section__header-container">
        {dragHandleProps && (
          <button
            className="collapsible-section__drag-handle"
            {...dragHandleProps}
            type="button"
            aria-label={`Drag ${title} section`}
          >
            ⋮⋮
          </button>
        )}
        <button
          className="collapsible-section__header"
          onClick={toggleExpanded}
          type="button"
          aria-expanded={isExpanded}
          aria-controls={`content-${sectionId}`}
        >
          <div className="collapsible-section__title-container">
            {icon && (
              <div className="collapsible-section__icon-container" aria-hidden="true">
                {icon}
              </div>
            )}
            <h2 className="collapsible-section__title">{title}</h2>
            {isEmpty && (
              <span className="collapsible-section__status" aria-label="Empty section">
                •
              </span>
            )}
          </div>
          <div className="collapsible-section__actions">
            {headerActions && (
              <div
                className="collapsible-section__header-actions"
                onClick={(e) => e.stopPropagation()} // Prevent collapse/expand when clicking actions
              >
                {headerActions}
              </div>
            )}
            <motion.span
              className="collapsible-section__chevron"
              variants={chevronVariants}
              animate={isExpanded ? 'expanded' : 'collapsed'}
              transition={{ duration: 0.2 }}
              aria-hidden="true"
            >
              ▼
            </motion.span>
          </div>
        </button>
      </div>

      {/* Content with consistent animations */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id={`content-${sectionId}`}
            className="collapsible-section__content"
            variants={sectionVariants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            transition={sectionTransition}
            style={{ overflow: 'hidden' }}
          >
            <div className="collapsible-section__inner">
              {isEmpty ? (
                <div className="collapsible-section__empty">
                  <p className="collapsible-section__empty-message">
                    {emptyMessage}
                  </p>
                </div>
              ) : (
                children
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CollapsibleSection;